import { Anchor, Button, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { i18n, useHelpContext } from "@dehub/theme";
import { WarningIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import useSWR from "swr";
import { ReturnApi, ReturnAnalyticsApi } from "../../../apis";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { ReturnKPICards } from "../../../components/return/kpi-cards";
import { ReturnStatusBadge, reasonLabel } from "../../../components/return/atoms";
import { DetailCard } from "../../../components/return/detail-card";
import { ListState } from "../../../components/ui";
import { formatDuration } from "../../../utils/format-duration";
import { currencyFormat } from "../../../utils/currency";
import { dateFormat } from "../../../utils/date";
import {
  TrendChart,
  ReasonBreakdownChart,
  RefundChannelBreakdownChart,
  TopProductsList,
  TopBuyersList
} from "../../../components/return/dashboard";

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

const breadcrumb = () => [
  { to: "/", label: i18n.t("returns:breadcrumb.home") },
  { label: i18n.t("returns:breadcrumb.returns") }
];

/** Recent returns as a table (canvas Ord_ReturnDash «Сүүлийн буцаалт»). */
function RecentReturns () {
  const { t } = useTranslation("returns");
  const { data, error, isLoading, mutate } = useSWR(
    ["returns.dashboard.recent"],
    () => ReturnApi.list({ page: 1, limit: 8, sortBy: "requestedAt", sortDir: "desc" }),
    { refreshInterval: 30_000, revalidateOnFocus: false }
  );
  const rows = data?.returns ?? [];

  return (
    <DetailCard
      title={t("dashboardPage.recentReturns")}
      helpKey="order.returns-dashboard.section.recent"
      extra={<Anchor component={Link} to="/returns" fw={600} fz="var(--nw-fz-body)" c="var(--nw-text)">{t("trustList.all")}</Anchor>}
    >
      {error && !data ? (
        <ListState state="error" onRetry={() => mutate()} />
      ) : isLoading && !data ? (
        <ListState state="loading" columns={6} rows={4} />
      ) : rows.length === 0 ? (
        <ListState state="empty" title={t("listTable.empty")} description={t("dashboardPage.recentEmpty")} />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
                <Table.Th>{t("col.buyer")}</Table.Th>
                <Table.Th>{t("filters.reasonLabel")}</Table.Th>
                <Table.Th>{t("detail.field.date")}</Table.Th>
                <Table.Th ta="right">{t("listTable.col.amount")}</Table.Th>
                <Table.Th>{t("listTable.col.status")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.returnRequestId}>
                  <Table.Td>
                    <Anchor component={Link} to={`/returns/${r.returnRequestId}`} ff="monospace" fw={700} fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">
                      {r.returnNumber}
                    </Anchor>
                  </Table.Td>
                  <Table.Td><Text size="sm">{r.buyerBusinessName || "-"}</Text></Table.Td>
                  {/* O196 — reason through i18n, never the enum. */}
                  <Table.Td><Text size="sm">{reasonLabel(r.returnReason) || "-"}</Text></Table.Td>
                  <Table.Td><Text size="sm" ff="monospace" style={TNUM}>{dateFormat(r.requestedAt)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{currencyFormat(r.totalRequestedAmount ?? 0)}</Text></Table.Td>
                  <Table.Td><ReturnStatusBadge status={r.status} help /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </DetailCard>
  );
}

export function ReturnDashboardPage (){
  const { t } = useTranslation("returns");
  // Every /return-analytics/* route checks RETURN_ANALYTICS_VIEW.
  const analytics = usePermission("RETURN_ANALYTICS_VIEW");
  const { data: dash, error, mutate } = useSWR(
    analytics.hasAccess ? ["returns.dashboard.analytics"] : null,
    () => ReturnAnalyticsApi.dashboard(),
    { refreshInterval: 30_000, revalidateOnFocus: false }
  );

  const counts = dash?.counts;
  const money  = dash?.money;
  const num = (v?: number) => (v == null ? "-" : v);
  const lit = (v?: number) => (v ?? 0) > 0;

  // @dehub/help: disputes and pending transfers drive the drawer's «Одоо» cards.
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ vars: { disputedCount: counts?.disputed ?? 0, refundPendingCount: counts?.refundPending ?? 0 } });
  }, [counts?.disputed, counts?.refundPending, setHelpContext]);

  return (
    <PageLayout
      title={t("breadcrumb.returns")}
      subTitle={t("dashboardPage.subtitle")}
      breadcrumb={breadcrumb()}
      extra={[
        (counts?.disputed ?? 0) > 0 ? (
          <Button key="disputes" component={Link} to="/returns/disputes" variant="default" leftSection={<WarningIcon size={18} />}>
            {t("dashboardPage.disputedWithCount", { count: counts?.disputed })}
          </Button>
        ) : null,
        <Anchor key="list" component={Link} to="/returns" fw={600} fz="var(--nw-fz-body)" c="var(--nw-text)">
          {t("dashboardPage.allList")}
        </Anchor>
      ]}
    >
      {!analytics.hasAccess ? (
        <ListState state="denied" permission="RETURN_ANALYTICS_VIEW" title={t("dashboardPage.deniedTitle")} />
      ) : (
        <Stack gap="md">
          {error && !dash ? (
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : (
            <ReturnKPICards
              cols={3}
              kpis={[
                // O195 — the dashboard counts REQUESTED/PROPOSED/DISPUTED/ACCEPTED/AWAITING_RESOLUTION
                // (analytics/dashboard.ts), not the list's «Идэвхтэй» tab: named «Шийдвэр хүлээж буй».
                { label: t("dashboardPage.kpi.awaitingDecision"), value: num(counts?.active), hint: t("dashboardPage.kpi.awaitingDecisionHint"), helpKey: "order.returns-dashboard.field.kpi-awaiting", tone: lit(counts?.active) ? "amber" : "grey" },
                { label: t("dashboardPage.kpi.disputed"), value: num(counts?.disputed), hint: t("dashboardPage.kpi.disputedHint"), helpKey: "order.returns-dashboard.field.kpi-disputed", tone: lit(counts?.disputed) ? "red" : "grey" },
                { label: t("dashboardPage.kpi.refundPending"), value: num(counts?.refundPending), hint: t("dashboardPage.kpi.refundPendingHint"), helpKey: "order.returns-dashboard.field.kpi-refund-pending", tone: lit(counts?.refundPending) ? "amber" : "grey" },
                { label: t("dashboardPage.kpi.resolved"), value: num(counts?.completed), hint: t("dashboard.widget.resolvedHint"), helpKey: "order.returns-dashboard.field.kpi-resolved", tone: lit(counts?.completed) ? "green" : "grey" },
                { label: t("dashboardPage.kpi.avgTime"), value: dash && dash.avgResolutionSeconds ? formatDuration(dash.avgResolutionSeconds) : "-", hint: t("dashboard.widget.avgResolveHint"), helpKey: "order.returns-dashboard.field.kpi-avg-time" },
                { label: t("dashboardPage.kpi.totalRefunded"), value: money ? currencyFormat(money.totalRefunded) : "-", hint: t("dashboardPage.kpi.totalRefundedHint"), helpKey: "order.returns-dashboard.field.kpi-total-refunded" }
              ]}
            />
          )}

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <TrendChart />
            <ReasonBreakdownChart />
            <RefundChannelBreakdownChart />
            <TopProductsList limit={5} />
          </SimpleGrid>
          <TopBuyersList limit={5} />
          <RecentReturns />
        </Stack>
      )}
    </PageLayout>
  );
}
